import React from 'react'

import RidingResultTableRow from './RidingResultTableRow'
import NationalResult from '../../../models/NationalResult'
import Party from '../../../models/Party'
import RidingResult from '../../../models/RidingResult'

type RidingSortFunction = (a: RidingResult, b: RidingResult) => number

interface IProps {
  nationalResult: NationalResult
}

interface IState {
  activeSortName?: string
  sortFunction?: RidingSortFunction
  reverse: boolean
}

class RidingResultTable extends React.Component<IProps, IState> {
  public constructor(props: IProps) {
    super(props)

    this.state = {
      activeSortName: undefined,
      sortFunction: undefined,
      reverse: false
    }
  }

  protected sortStyle(accessorName: string): string {
    if (accessorName === this.state.activeSortName) {
      return this.state.reverse ? '↑' : '↓'
    }
    return ''
  }

  protected setSortFunction(accessorName: string): void {
    const reverse =
      accessorName === this.state.activeSortName ? !this.state.reverse : false

    let sortFunction: RidingSortFunction | undefined = undefined

    if (accessorName === 'province') {
      sortFunction = (a, b): number =>
        `${a.riding.province.name()}-${a.riding.name()}`.localeCompare(
          `${b.riding.province.name()}-${b.riding.name()}`
        )
    } else if (accessorName === 'ridingName') {
      sortFunction = (a, b): number =>
        a.riding.name().localeCompare(b.riding.name())
    } else if (Party.allPartyKeys().includes(accessorName)) {
      sortFunction = (a, b): number => {
        const partyAResult = a.partyResultByPartyKey(accessorName)
        const partyAValue = partyAResult ? partyAResult.votePercent : 0
        const partyBResult = b.partyResultByPartyKey(accessorName)
        const partyBValue = partyBResult ? partyBResult.votePercent : 0
        return partyBValue - partyAValue
      }
    } else if (accessorName === 'winner') {
      sortFunction = (a, b): number =>
        a.winner().party.name.localeCompare(b.winner().party.name)
    } else if (accessorName === 'runnerUp') {
      sortFunction = (a, b): number =>
        a.nthPlace(2).party.name.localeCompare(b.nthPlace(2).party.name)
    } else if (accessorName === 'winPercent') {
      sortFunction = (a, b): number =>
        a.winner().votePercent - b.winner().votePercent
    } else if (accessorName === 'margin' || accessorName === 'close') {
      sortFunction = (a, b): number => a.margin() - b.margin()
    } else if (accessorName === 'multi') {
      sortFunction = (a, b): number =>
        a.winner().votePercent - b.winner().votePercent
    }
    if (sortFunction) {
      // const reverse = this.state.sortFunction === sortFunction ? true : false
      this.setState({ activeSortName: accessorName, sortFunction, reverse })
    }
  }

  public render(): JSX.Element {
    const { nationalResult } = this.props
    const { sortFunction, reverse } = this.state

    const tmpRidingRows = nationalResult.ridingResults().sort(sortFunction)

    if (reverse) tmpRidingRows.reverse()

    const ridingRows = tmpRidingRows.map(
      (ridingResult): JSX.Element => (
        <RidingResultTableRow
          key={ridingResult.riding.key}
          ridingResult={ridingResult}
        />
      )
    )

    const partyHeaders = Party.allPartiesArray().map(
      (party): JSX.Element => (
        <th
          key={party.key}
          onClick={(): void => this.setSortFunction(party.key)}
          className="d-none d-md-table-cell text-right"
        >
          {party.key}
          {this.sortStyle(party.key)}
        </th>
      )
    )

    return (
      <div>
        <h2>Riding-by-riding results</h2>
        <div className="Legend col text-right mb-2 text-muted">
          <small>
            <span className="mr-3">
              <span className="d-sm-none">
                <span className="font-weight-bold">Cl</span> close;{' '}
              </span>
              <span className="d-none d-sm-inline font-weight-bold">
                Close{' '}
              </span>
              margin &lt; 5%
            </span>
            <span>
              <span className="d-sm-none font-weight-bold">Mu </span>
              <span className="d-none d-sm-inline font-weight-bold">
                Multi{' '}
              </span>
              multi-way; win % &lt; 35%
            </span>
          </small>
        </div>
        <table className="table">
          <thead>
            <tr className="SortableHeaders">
              <th onClick={(): void => this.setSortFunction('province')}>
                Pr{this.sortStyle('province')}
              </th>
              {/* <th>ED Number</th> */}
              <th onClick={(): void => this.setSortFunction('ridingName')}>
                Riding
                {this.sortStyle('ridingName')}
              </th>
              {partyHeaders}
              <th
                className="text-center"
                onClick={(): void => this.setSortFunction('winner')}
              >
                Winner
                {this.sortStyle('winner')}
              </th>
              <th
                className="text-right"
                onClick={(): void => this.setSortFunction('winPercent')}
              >
                Win&nbsp;%{this.sortStyle('winPercent')}
              </th>
              <th
                className="text-center"
                onClick={(): void => this.setSortFunction('runnerUp')}
              >
                <span className="d-none d-sm-inline">Runner&#x2011;up</span>
                <span className="d-sm-none">
                  2<sup>nd</sup>
                </span>
                {this.sortStyle('runnerUp')}
              </th>
              <th
                className="text-right"
                onClick={(): void => this.setSortFunction('margin')}
              >
                Margin
                {this.sortStyle('margin')}
              </th>
              <th
                className="text-center"
                onClick={(): void => this.setSortFunction('close')}
              >
                <span className="d-none d-sm-inline">Close</span>
                <span className="d-sm-none">
                  <abbr title="Close race">Cl</abbr>
                </span>
                {this.sortStyle('close')}
              </th>
              <th
                className="text-center"
                onClick={(): void => this.setSortFunction('multi')}
              >
                <span className="d-none d-sm-inline">Multi</span>
                <span className="d-sm-none">
                  <abbr title="Multi-way race">Mu</abbr>
                </span>
                {this.sortStyle('multi')}
              </th>
            </tr>
          </thead>
          <tbody>{ridingRows}</tbody>
        </table>
      </div>
    )
  }
}

export default RidingResultTable
